import React, { useState, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  Button,
  Tabs,
  Tab,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Download as DownloadIcon,
  AutoFixHigh as GenerateIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import Editor from '@monaco-editor/react';

interface CodeEditorProps {
  code: string;
  language: 'typescript' | 'python' | 'json';
  onChange: (code: string) => void;
  onGenerate?: () => void;
  title: string;
}

export const CodeEditor: React.FC<CodeEditorProps> = ({
  code,
  language,
  onChange,
  onGenerate,
  title
}) => {
  const [fullscreen, setFullscreen] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const editorRef = useRef<any>(null);
  
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
  };
  
  const handleDownload = () => {
    const extension = language === 'typescript' ? 'tsx' : language === 'python' ? 'py' : 'json';
    const blob = new Blob([code], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `node-code.${extension}`;
    a.click();
    URL.revokeObjectURL(url);
  };
  
  const handleEditorChange = (value: string | undefined) => {
    if (value !== undefined) {
      onChange(value);
    }
  };
  
  const handleFormat = () => {
    if (editorRef.current) {
      editorRef.current.getAction('editor.action.formatDocument').run();
    }
  };
  
  const getLanguageConfig = () => {
    switch (language) {
      case 'typescript':
        return {
          language: 'typescript',
          theme: 'vs-dark',
          options: {
            automaticLayout: true,
            minimap: { enabled: true },
            scrollBeyondLastLine: false,
            fontSize: 14,
            tabSize: 2,
            insertSpaces: true,
            formatOnPaste: true,
            formatOnType: true,
          }
        };
      case 'python':
        return {
          language: 'python',
          theme: 'vs-dark',
          options: {
            automaticLayout: true,
            minimap: { enabled: true },
            scrollBeyondLastLine: false,
            fontSize: 14,
            tabSize: 4,
            insertSpaces: true,
            formatOnPaste: true,
            formatOnType: true,
          }
        };
      case 'json':
        return {
          language: 'json',
          theme: 'vs-dark',
          options: {
            automaticLayout: true,
            minimap: { enabled: false },
            scrollBeyondLastLine: false,
            fontSize: 14,
            tabSize: 2,
          }
        };
    }
  };
  
  const config = getLanguageConfig();
  
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Toolbar */}
      <Paper sx={{ p: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle1">{title}</Typography>
          <Chip size="small" label={language.toUpperCase()} color="primary" />
        </Box>
        
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {onGenerate && (
            <Tooltip title="Generate with AI">
              <Button
                size="small"
                startIcon={<GenerateIcon />}
                onClick={onGenerate}
                variant="outlined"
                color="secondary"
              >
                Generate
              </Button>
            </Tooltip>
          )}
          
          <Tooltip title="Format code">
            <IconButton size="small" onClick={handleFormat}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Copy to clipboard">
            <IconButton size="small" onClick={handleCopy}>
              <CopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Download">
            <IconButton size="small" onClick={handleDownload}>
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          
          <Tooltip title={fullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
            <IconButton size="small" onClick={() => setFullscreen(!fullscreen)}>
              {fullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>
      
      {/* Editor */}
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        <Editor
          height="100%"
          language={config.language}
          theme={config.theme}
          value={code}
          onChange={handleEditorChange}
          onMount={(editor) => { editorRef.current = editor; }}
          options={config.options}
        />
      </Box>
      
      {/* Fullscreen Dialog */}
      <Dialog
        open={fullscreen}
        onClose={() => setFullscreen(false)}
        maxWidth="xl"
        fullWidth
        fullScreen
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6">{title}</Typography>
            <Chip size="small" label={language.toUpperCase()} color="primary" />
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {onGenerate && (
              <Button
                startIcon={<GenerateIcon />}
                onClick={onGenerate}
                variant="outlined"
                color="secondary"
              >
                Generate
              </Button>
            )}
            <IconButton onClick={() => setFullscreen(false)}>
              <FullscreenExitIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Editor
            height="calc(100vh - 120px)"
            language={config.language}
            theme={config.theme}
            value={code}
            onChange={handleEditorChange}
            onMount={(editor) => { editorRef.current = editor; }}
            options={config.options}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCopy} startIcon={<CopyIcon />}>
            Copy
          </Button>
          <Button onClick={handleDownload} startIcon={<DownloadIcon />}>
            Download
          </Button>
          <Button onClick={() => setFullscreen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CodeEditor;
